import React, { useState, useEffect } from "react";
import API_URL from "../config";
import axios from "axios";
import { Link } from "react-router-dom";

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const PROGRAMS_PER_PAGE = 6;

  const categories = [
    "All",
    "Foundations",
    "Productivity & Automation",
    "Leadership & Strategy",
    "Governance & Compliance",
    "Business Growth",
    "Security & Ethics",
    "Trends & Innovation",
    "Education",
    "Healthcare",
  ];

  const statuses = ["All", "Open", "Closed"];

  // Fetch programs
  useEffect(() => {
    axios
      .get(`${API_URL}/api/programs`)
      .then((response) => {
        setPrograms(response.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load programs.");
        setLoading(false);
      });
  }, []);

  // Apply filters
  useEffect(() => {
    const filtered = programs.filter((program) => {
      const categoryMatch =
        categoryFilter === "All" ||
        (Array.isArray(program.category)
          ? program.category.includes(categoryFilter)
          : program.category === categoryFilter);

      const statusMatch =
        statusFilter === "All" || program.status === statusFilter;

      return categoryMatch && statusMatch;
    });

    setFilteredPrograms(filtered);
    setCurrentPage(1);
  }, [programs, categoryFilter, statusFilter]);

  const clearFilters = () => {
    setCategoryFilter("All");
    setStatusFilter("All");
  };

  const totalPages = Math.ceil(filteredPrograms.length / PROGRAMS_PER_PAGE) || 1;

  const currentPrograms = filteredPrograms.slice(
    (currentPage - 1) * PROGRAMS_PER_PAGE,
    currentPage * PROGRAMS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Loading state
  if (loading) {
    return (
      <section className="bg-gray-50 text-black py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">Loading programs...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="bg-gray-50 text-black py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm sm:text-base text-red-600">
            Error loading programs: {error}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gray-50 text-black py-10 sm:py-20 lg:py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-thin tracking-wide mb-4 sm:mb-6">
            Professional <span className="text-gray-500">Programs</span>
          </h2>
          <div className="w-24 sm:w-32 h-px bg-black mx-auto mb-4 sm:mb-6" />
          <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Curated AI programs designed for professionals, teams, and leaders
            who want structured learning and real, measurable outcomes.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-10 sm:mb-12 space-y-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Category Filter */}
            <div>
              <h3 className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-gray-500 mb-3 sm:mb-4">
                Category
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setCategoryFilter(category)}
                    className={`px-4 py-2 text-xs sm:text-sm border rounded-full transition-colors ${
                      categoryFilter === category
                        ? "border-black bg-black text-white"
                        : "border-gray-300 text-gray-500 hover:border-black hover:text-black"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <h3 className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-gray-500 mb-3 sm:mb-4">
                Status
              </h3>
              <div className="flex flex-wrap gap-2">
                {statuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 text-xs sm:text-sm border rounded-full transition-colors ${
                      statusFilter === status
                        ? "border-black bg-black text-white"
                        : "border-gray-300 text-gray-500 hover:border-black hover:text-black"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={clearFilters}
            className="text-xs sm:text-sm text-gray-400 hover:text-black transition-colors underline"
          >
            Clear all filters
          </button>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10 sm:mb-12">
          {currentPrograms.length > 0 ? (
            currentPrograms.map((program) => {
              const shortDescription =
                (program.description || "").length > 220
                  ? `${program.description.slice(0, 220)}…`
                  : program.description || "No description available.";

              return (
                <Link
                  to={`/programs/${program.slug}`}
                  key={program.slug}
                  className="cursor-pointer h-full flex"
                >
                  <div className="border border-gray-300 p-6 sm:p-8 hover:border-black transition-colors group flex flex-col h-full bg-white">
                    <div className="flex justify-between items-start mb-4 sm:mb-6">
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-lg font-light tracking-wide mb-2 group-hover:text-gray-900 transition-colors">
                          {program.title}
                        </h3>
                        <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-800 space-x-2 sm:space-x-3">
                          {program.duration && <span>{program.duration}</span>}
                          {program.duration && program.format && <span>|</span>}
                          {program.format && <span>{program.format}</span>}
                        </div>
                      </div>
                      <div
                        className={`px-2 py-1 text-[10px] sm:text-xs border rounded ${
                          program.status === "Open"
                            ? "border-green-500 text-emerald-900 bg-emerald-50"
                            : "border-red-500 text-red-800 bg-red-50"
                        }`}
                      >
                        {program.status}
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-sm">
                      {shortDescription}
                    </p>
                  </div>
                </Link>
              );
            })
          ) : (
            <div className="col-span-full text-center text-gray-400 text-sm sm:text-base">
              No programs match your filters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-3 sm:space-x-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-xs sm:text-sm border border-gray-400 text-gray-500 hover:border-black hover:text-black disabled:opacity-40 disabled:cursor-not-allowed rounded"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => goToPage(i + 1)}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm border rounded transition-colors ${
                  currentPage === i + 1
                    ? "border-black bg-black text-white"
                    : "border-gray-300 text-gray-500 hover:border-black hover:text-black"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-xs sm:text-sm border border-gray-400 text-gray-500 hover:border-black hover:text-black disabled:opacity-40 disabled:cursor-not-allowed rounded"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Programs;
